import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  loginAdminSendOTP,
  loginAdminVerifyOTP,
  loginStaffSendOTP,
  loginStaffVerifyOTP,
  forgotPasswordSendOTP,
  forgotPasswordVerifyOTPAndReset,
  forgotStaffPasswordSendOTP,
  forgotStaffPasswordVerifyOTPAndReset,
  loginAdminVerifyTOTP,
} from "../api/authApi";
import { loginSuccess } from "../redux/authSlice";
import LogoName from "../assets/images/logo_name.png";
import "./css/Login.css";
import { socket } from "../socket";
import { toast } from "react-toastify";
import { loginStaffVerifyTOTP } from "../api/staffApi";

// ─── Login Flow (2-step, OTP-based) ─────────────────────────────────────────
// Step 1 — POST .../login              { email, password } → sends 6-digit OTP
// Step 2 — POST .../login/verify-otp   { email, otp }      → returns real token
// Applies to BOTH Organization (admin) and Staff logins.

// ─── Forgot Password Flow ──────────────────────────────────────────────────
// Step 1: POST /api/auth/admin/forgot-password/send-otp   { email }
// Step 2: POST /api/auth/admin/forgot-password/verify-otp-reset { email, otp, newPassword }

const Login = () => {
  // "organization" | "staff"
  const otpInputRef = useRef(null);
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  const [loginType, setLoginType] = useState("organization");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // ── Login-OTP state (step 2 of login) ────────────────────────────────────
  const [loginOtp, setLoginOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpResendLoading, setOtpResendLoading] = useState(false);

  // Forgot password state
  // "login" | "otp_login" | "totp_login" | "forgot_email" | "forgot_otp"
  const [view, setView] = useState("login");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const dispatch = useDispatch();

  // ✅ FIX: useEffect must run on every render, BEFORE any early `return`
  // statements below (otp_login / totp_login / forgot_email / forgot_otp
  // views). Keeping it here guarantees the same hook order every time —
  // fixes "Rendered fewer hooks than expected".
  useEffect(() => {
    if (token) {
      // If token exists, redirect to home page
      console.log(role, "role from local storage");

      if (role === "SUPERADMIN" || role === "ADMIN") {
        window.location.href = "/";
      } else if (role === "HR") {
        window.location.href = "/drivers";
      } else if (role === "STAFF") {
        window.location.href = "/staff-dashboard";
      } else if (role === "BDE") {
        window.location.href = "/bde-dashboard";
      }
    }
  }, [token, role]);

  // ✅ FIX: this MUST also sit above every early return below — it was
  // previously placed after the `if (view === "totp_login") return (...)`
  // block, which meant on some renders this hook never ran at all →
  // "Rendered fewer hooks than expected". Same rule as the effect above:
  // every hook, every render, same order, no exceptions.
  useEffect(() => {
    if (view === "otp_login" || view === "totp_login") {
      otpInputRef.current?.focus();
    }
  }, [view]);

  // Switch tab → reset fields & error
  const handleTabSwitch = (type) => {
    console.log(type, "login type");
    setLoginType(type);
    setEmail(type === "organization" ? "" : "");
    setPassword(type === "organization" ? "" : "");
    setError("");
  };

  // ── Step 1: verify email/password → triggers OTP email ───────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const data =
        loginType === "organization"
          ? await loginAdminSendOTP({ email, password })
          : await loginStaffSendOTP({ email, password });

      if (data.success) {
        // ✅ REVIEWER BYPASS — payment-gateway (Razorpay) / app-store review
        // forms only ask for email + password, there's no OTP field for the
        // reviewer to fill in. For that one whitelisted account, skip the
        // OTP screen entirely and auto-verify with the fixed reviewer OTP
        // (backend issues this same static OTP for this email — see
        // authController.js loginAdmin REVIEWER_BYPASS_* env vars).
        const reviewerEmail = process.env.REACT_APP_REVIEWER_BYPASS_EMAIL;
        const reviewerOtp = process.env.REACT_APP_REVIEWER_BYPASS_OTP;
        const isReviewerAccount =
          loginType === "organization" &&
          reviewerEmail &&
          reviewerOtp &&
          email.toLowerCase() === reviewerEmail.toLowerCase();

        if (isReviewerAccount) {
          try {
            const verifyData = await loginAdminVerifyOTP({
              email,
              otp: reviewerOtp,
            });
            if (verifyData.success) {
              completeLogin(verifyData);
              return;
            }
            // Fall through to normal OTP screen if the bypass didn't work
            // (e.g. env vars out of sync with backend) so login isn't blocked.
          } catch (bypassErr) {
            // Same fallback on network/server error — don't strand the user.
          }
        }

        // ✅ NEW — 2FA (TOTP) enabled account
        if (data.totpRequired) {
          setView("totp_login");
          return;
        }

        toast.success(data.message || "OTP sent to your email");
        setLoginOtp("");
        setOtpError("");
        setView("otp_login");
      } else {
        toast.error(data.message || "Invalid credentials, please try again.");
        setError(data.message || "Invalid credentials, please try again.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Server error, please try later.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Shared success handler — used by both normal OTP verify AND the
  // reviewer auto-login bypass below, so redirect/socket/dispatch logic
  // lives in exactly one place. ─────────────────────────────────────────────
  const completeLogin = (data) => {
    toast.success("Login successful!");

    if (loginType === "organization") {
      dispatch(loginSuccess({ token: data.token, admin: data.admin }));

      const adminId = data.admin?._id || data.admin?.roleId || "admin_1";
      socket.connect();
      socket.emit("admin-online", { adminId });
      window.location.href = "/";
    } else {
      dispatch(
        loginSuccess({
          token: data.token,
          admin: {
            ...data.staff,
            role: data.staff.role || "STAFF",
          },
        }),
      );

      const staffId = data.staff?._id || "staff_1";
      socket.connect();
      socket.emit("staff-online", { staffId });

      console.log(data.staff.role, "staff role");
      if (data.staff.role === "STAFF") {
        window.location.href = "/staff-dashboard";
      } else if (data.staff.role === "BDE") {
        window.location.href = "/bde-dashboard";
      } else if (data.staff.role === "HR") {
        window.location.href = "/drivers";
      } else {
        window.location.href = "/";
      }
    }
  };

  // ── Step 2: verify OTP → real login, then redirect ────────────────────────
  const handleVerifyLoginOtp = async (e) => {
    e.preventDefault();
    setOtpLoading(true);
    setOtpError("");

    try {
      const data =
        loginType === "organization"
          ? await loginAdminVerifyOTP({ email, otp: loginOtp })
          : await loginStaffVerifyOTP({ email, otp: loginOtp });

      if (data.success) {
        completeLogin(data);
      } else {
        setOtpError(data.message || "Invalid OTP. Please try again.");
        toast.error(data.message || "Invalid OTP. Please try again.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Server error, please try later.";
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Resend login OTP — reuses the email/password already entered ─────────
  const handleResendLoginOtp = async () => {
    setOtpResendLoading(true);
    setOtpError("");
    try {
      const data =
        loginType === "organization"
          ? await loginAdminSendOTP({ email, password })
          : await loginStaffSendOTP({ email, password });

      if (data.success) {
        toast.success("OTP resent to your email");
      } else {
        setOtpError(data.message || "Failed to resend OTP.");
        toast.error(data.message || "Failed to resend OTP.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Server error, please try later.";
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setOtpResendLoading(false);
    }
  };

  const handleBackToLoginFromOtp = () => {
    setView("login");
    setLoginOtp("");
    setOtpError("");
  };

  // ── Forgot Password: Step 1 — Send OTP ───────────────────────────────────
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError("");
    setForgotSuccess("");
    console.log(loginType, "login type in forgot password");
    try {
      const data =
        loginType === "organization"
          ? await forgotPasswordSendOTP({ email: forgotEmail })
          : await forgotStaffPasswordSendOTP({ email: forgotEmail });
      if (data.success) {
        setForgotSuccess("OTP sent to your email. Check your inbox.");
        toast.success("OTP sent to your email. Check your inbox.");
        setView("forgot_otp");
      } else {
        setForgotError(data.message || "Failed to send OTP. Try again.");
        toast.error(data.message || "Failed to send OTP. Try again.");
      }
    } catch (err) {
      setForgotError(
        err?.response?.data?.message || "Server error. Please try later.",
      );
      toast.error(
        err?.response?.data?.message || "Server error. Please try later.",
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // ── Step 2 (TOTP variant): verify authenticator code → real login ─────────
  const handleVerifyTotp = async (e) => {
    e.preventDefault();
    setOtpLoading(true);
    setOtpError("");

    try {
      const data =
        loginType === "organization"
          ? await loginAdminVerifyTOTP({ email, token: loginOtp })
          : await loginStaffVerifyTOTP({ email, token: loginOtp });

      if (data.success) {
        completeLogin(data);
      } else {
        setOtpError(data.message || "Invalid code. Please try again.");
        toast.error(data.message || "Invalid code. Please try again.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Server error, please try later.";
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Forgot Password: Step 2 — Verify OTP & Reset ─────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotSuccess("");

    if (newPassword !== confirmPassword) {
      setForgotError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setForgotError("Password must be at least 6 characters.");
      return;
    }

    setForgotLoading(true);
    try {
      const data =
        loginType === "organization"
          ? await forgotPasswordVerifyOTPAndReset({
              email: forgotEmail,
              otp: forgotOtp,
              newPassword,
            })
          : await forgotStaffPasswordVerifyOTPAndReset({
              email: forgotEmail,
              otp: forgotOtp,
              newPassword,
            });
      if (data.success) {
        setForgotSuccess("Password reset successful! You can now login.");
        toast.success("Password reset successful! You can now login.");
        // Auto-redirect back to login after 2s
        setTimeout(() => {
          setView("login");
          setForgotEmail("");
          setForgotOtp("");
          setNewPassword("");
          setConfirmPassword("");
          setForgotSuccess("");
        }, 2000);
      } else {
        setForgotError(data.message || "Invalid OTP. Please try again.");
        toast.error(data.message || "Invalid OTP. Please try again.");
      }
    } catch (err) {
      setForgotError(
        err?.response?.data?.message || "Server error. Please try later.",
      );
      toast.error(
        err?.response?.data?.message || "Server error. Please try later.",
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setView("login");
    setForgotEmail("");
    setForgotOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setForgotError("");
    setForgotSuccess("");
  };

  // ── Render: Login OTP Step ────────────────────────────────────────────────
  if (view === "otp_login") {
    return (
      <div className="login-wrapper">
        <div className="login-card">
          <img src={LogoName} alt="Moovuruli Logo" className="login-logo" />
          <h2 className="login-title">Verify OTP</h2>
          <p
            style={{
              textAlign: "center",
              color: "#666",
              fontSize: "14px",
              marginBottom: "4px",
            }}
          >
            6-digit OTP sent to <strong>{email}</strong>
          </p>

          <form onSubmit={handleVerifyLoginOtp} autoComplete="off">
            <input
              ref={otpInputRef}
              type="text"
              placeholder="Enter 6-digit OTP"
              value={loginOtp}
              onChange={(e) => setLoginOtp(e.target.value.replace(/\D/g, ""))}
              required
              className="login-input"
              maxLength={6}
              autoComplete="off"
              inputMode="numeric"
            />

            {otpError && <p className="error-text">{otpError}</p>}

            <button type="submit" disabled={otpLoading} className="login-btn">
              {otpLoading ? "Verifying..." : "Verify & Login"}
            </button>

            <button
              type="button"
              onClick={handleResendLoginOtp}
              disabled={otpResendLoading}
              style={{
                background: "none",
                border: "none",
                color: "#555",
                cursor: "pointer",
                display: "block",
                margin: "12px auto 0",
                fontSize: "13px",
                textDecoration: "underline",
              }}
            >
              {otpResendLoading ? "Resending..." : "Resend OTP"}
            </button>

            <button
              type="button"
              onClick={handleBackToLoginFromOtp}
              style={{
                background: "none",
                border: "none",
                color: "#555",
                cursor: "pointer",
                display: "block",
                margin: "8px auto 0",
                fontSize: "13px",
                textDecoration: "underline",
              }}
            >
              ← Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Render: Forgot Password - Email Step ─────────────────────────────────
  if (view === "forgot_email") {
    return (
      <div className="login-wrapper">
        <div className="login-card">
          <img src={LogoName} alt="Moovuruli Logo" className="login-logo" />
          <h2 className="login-title">Forgot Password</h2>
          <p
            style={{
              textAlign: "center",
              color: "#666",
              fontSize: "14px",
              marginBottom: "16px",
            }}
          >
            Enter your registered email. We'll send an OTP to reset your
            password.
          </p>

          <form onSubmit={handleSendOTP} autoComplete="off">
            <input
              type="email"
              placeholder="Enter Registered Email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              required
              className="login-input"
              autoComplete="off"
            />

            {forgotError && <p className="error-text">{forgotError}</p>}
            {forgotSuccess && (
              <p style={{ color: "green", fontSize: "13px", marginTop: "8px" }}>
                {forgotSuccess}
              </p>
            )}

            <button
              type="submit"
              disabled={forgotLoading}
              className="login-btn"
            >
              {forgotLoading ? "Sending OTP..." : "Send OTP"}
            </button>

            <button
              type="button"
              onClick={handleBackToLogin}
              style={{
                background: "none",
                border: "none",
                color: "#555",
                cursor: "pointer",
                display: "block",
                margin: "12px auto 0",
                fontSize: "13px",
                textDecoration: "underline",
              }}
            >
              ← Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Render: Forgot Password - OTP + New Password Step ────────────────────
  if (view === "forgot_otp") {
    return (
      <div className="login-wrapper">
        <div className="login-card">
          <img src={LogoName} alt="Moovuruli Logo" className="login-logo" />
          <h2 className="login-title">Reset Password</h2>
          <p
            style={{
              textAlign: "center",
              color: "#666",
              fontSize: "14px",
              marginBottom: "4px",
            }}
          >
            OTP sent to <strong>{forgotEmail}</strong>
          </p>

          <form onSubmit={handleResetPassword} autoComplete="off">
            <input
              type="text"
              placeholder="Enter OTP"
              value={forgotOtp}
              onChange={(e) => setForgotOtp(e.target.value)}
              required
              className="login-input"
              maxLength={6}
              autoComplete="off"
            />

            <div className="password-field">
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="login-input"
              />
              <span
                className="eye-icon"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? "🙈" : "👁️"}
              </span>
            </div>

            <div className="password-field">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="login-input"
              />
              <span
                className="eye-icon"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? "🙈" : "👁️"}
              </span>
            </div>

            {forgotError && <p className="error-text">{forgotError}</p>}
            {forgotSuccess && (
              <p style={{ color: "green", fontSize: "13px", marginTop: "8px" }}>
                {forgotSuccess}
              </p>
            )}

            <button
              type="submit"
              disabled={forgotLoading}
              className="login-btn"
            >
              {forgotLoading ? "Resetting..." : "Reset Password"}
            </button>

            {/* Resend OTP */}
            <button
              type="button"
              onClick={() => setView("forgot_email")}
              style={{
                background: "none",
                border: "none",
                color: "#555",
                cursor: "pointer",
                display: "block",
                margin: "8px auto 0",
                fontSize: "13px",
                textDecoration: "underline",
              }}
            >
              Resend OTP
            </button>

            <button
              type="button"
              onClick={handleBackToLogin}
              style={{
                background: "none",
                border: "none",
                color: "#555",
                cursor: "pointer",
                display: "block",
                margin: "8px auto 0",
                fontSize: "13px",
                textDecoration: "underline",
              }}
            >
              ← Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Render: Login TOTP Step (Authenticator app code) ───────────────────────
  if (view === "totp_login") {
    return (
      <div className="login-wrapper">
        <div className="login-card">
          <img src={LogoName} alt="Moovuruli Logo" className="login-logo" />
          <h2 className="login-title">Enter Authenticator Code</h2>
          <p
            style={{
              textAlign: "center",
              color: "#666",
              fontSize: "14px",
              marginBottom: "4px",
            }}
          >
            Open Google Authenticator and enter the 6-digit code for{" "}
            <strong>{email}</strong>
          </p>

          <form onSubmit={handleVerifyTotp} autoComplete="off">
            <input
              ref={otpInputRef}
              type="text"
              placeholder="Enter 6-digit code"
              value={loginOtp}
              onChange={(e) => setLoginOtp(e.target.value.replace(/\D/g, ""))}
              required
              className="login-input"
              maxLength={6}
              autoComplete="off"
              inputMode="numeric"
            />

            {otpError && <p className="error-text">{otpError}</p>}

            <button type="submit" disabled={otpLoading} className="login-btn">
              {otpLoading ? "Verifying..." : "Verify & Login"}
            </button>

            <button
              type="button"
              onClick={handleBackToLoginFromOtp}
              style={{
                background: "none",
                border: "none",
                color: "#555",
                cursor: "pointer",
                display: "block",
                margin: "8px auto 0",
                fontSize: "13px",
                textDecoration: "underline",
              }}
            >
              ← Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Render: Normal Login ──────────────────────────────────────────────────
  return (
    <div className="login-wrapper">
      <div className="login-card">
        <img src={LogoName} alt="Moovuruli Logo" className="login-logo" />
        <h2 className="login-title">
          {loginType === "organization" ? "Organization Login" : "Staff Login"}
        </h2>

        {/* ── Tab Switch ── */}
        <div className="login-tabs">
          <button
            type="button"
            className={`login-tab ${loginType === "organization" ? "active" : ""}`}
            onClick={() => handleTabSwitch("organization")}
          >
            Organization
          </button>
          <button
            type="button"
            className={`login-tab ${loginType === "staff" ? "active" : ""}`}
            onClick={() => handleTabSwitch("staff")}
          >
            Staff
          </button>
        </div>

        <form onSubmit={handleLogin} autoComplete="off">
          <input
            type="email"
            placeholder="Enter Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="login-input"
            autoComplete="off"
          />

          <div className="password-field">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="login-input"
            />
            <span
              className="eye-icon"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "🙈" : "👁️"}
            </span>
          </div>

          {/* ── Forgot Password Link — only on Organization tab ── */}

          {error && <p className="error-text">{error}</p>}

          <button type="submit" disabled={loading} className="login-btn">
            {loading ? "Sending OTP..." : "Login"}
          </button>

          {loginType === "organization" && (
            <div
              style={{
                textAlign: "right",
                marginTop: "10px",
                marginBottom: "8px",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setView("forgot_email");
                  setForgotEmail(email); // pre-fill with whatever they typed
                  setForgotError("");
                  setForgotSuccess("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#1a73e8",
                  cursor: "pointer",
                  fontSize: "13px",
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                Forgot Password?
              </button>
            </div>
          )}

          {loginType === "staff" && (
            <div
              style={{
                textAlign: "right",
                marginTop: "10px",
                marginBottom: "8px",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setView("forgot_email");
                  setForgotEmail(email);
                  setForgotError("");
                  setForgotSuccess("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#1a73e8",
                  cursor: "pointer",
                  fontSize: "13px",
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                Forgot Password?
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
